import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { DomainBaseComponent } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { InvoiceDialogComponent } from 'app/shared/invoice-dialog.component';
import { UtilFns } from 'app/shared/util-fns';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

interface TestableInvoice {
  invoiceId: string;
  glInvoiceId: string;
  status: string;
  accountName: string;
  createdDate: string;
  subtotalAmt: number | null;
  taxAmt: number | null;
  freightAmt: number | null;
  handlingAmt: number | null;
  totalAmt: number | null;
}

interface GetTestableInvoicesResponse {
  status: string;
  count: number;
  invoices: TestableInvoice[];
}

interface PostInvoiceResponse {
  status: string;
  message: string;
  invoiceId: string;
  glInvoiceId?: string;
  invoiceStatus?: string;
  accountName?: string;
}

interface StatusFilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-qbo-test',
  templateUrl: './qbo-test.component.html',
  standalone: false
})
export class QboTestComponent extends DomainBaseComponent {
  private http = inject(HttpClient);

  gridOptions: GridOptionsExt;
  invoices: TestableInvoice[] = [];
  selectedInvoice: TestableInvoice | null = null;
  isLoading = false;
  lastResult: PostInvoiceResponse | null = null;
  invoiceCount = 20;

  // Status filter
  statusFilterOptions: StatusFilterOption[] = [
    { label: 'Posted (Re-sync)', value: 'posted' },
    { label: 'Unposted (New Post)', value: 'unposted' },
  ];
  selectedStatusFilter: StatusFilterOption = this.statusFilterOptions[1]; // Default to unposted

  constructor(protected domainService: DomainService) {
    super(domainService);
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onRowSelected,
      rowSelection: { mode: 'singleRow' },
      getRowId: (params: GetRowIdParams) => params.data?.invoiceId,  // Use invoiceId as unique key
    });
    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...AgFns.createButtonProps('', this.openInvoiceDialog.bind(this), { label: 'View' }),
        width: 70, maxWidth: 70 },
      { headerName: 'Invoice ID', field: 'invoiceId', filter: 'agTextColumnFilter', width: 140 },
      { headerName: 'GL Invoice ID', field: 'glInvoiceId', filter: 'agTextColumnFilter', width: 120 },
      { headerName: 'Status', field: 'status', filter: 'agTextColumnFilter', width: 110 },
      { headerName: 'Account', field: 'accountName', filter: 'agTextColumnFilter', flex: 1 },
      { headerName: 'Created', field: 'createdDate', filter: 'agDateColumnFilter', width: 160,
        valueFormatter: (p: any) => p.value ? new Date(p.value).toLocaleString() : '' },
      { headerName: 'Subtotal', field: 'subtotalAmt', filter: 'agNumberColumnFilter', width: 100,
        valueFormatter: (p: any) => UtilFns.fmtCurrency(p.value), type: 'numericColumn' },
      { headerName: 'Tax', field: 'taxAmt', filter: 'agNumberColumnFilter', width: 90,
        valueFormatter: (p: any) => UtilFns.fmtCurrency(p.value), type: 'numericColumn' },
      { headerName: 'Freight', field: 'freightAmt', filter: 'agNumberColumnFilter', width: 90,
        valueFormatter: (p: any) => UtilFns.fmtCurrency(p.value), type: 'numericColumn' },
      { headerName: 'Handling', field: 'handlingAmt', filter: 'agNumberColumnFilter', width: 90,
        valueFormatter: (p: any) => UtilFns.fmtCurrency(p.value), type: 'numericColumn' },
      { headerName: 'Total', field: 'totalAmt', filter: 'agNumberColumnFilter', width: 100,
        valueFormatter: (p: any) => UtilFns.fmtCurrency(p.value), type: 'numericColumn' },
    ];
    AgFns.initGrid(event, colDefs);
  }

  async openInvoiceDialog(invoice: TestableInvoice) {
    await InvoiceDialogComponent.openDialog(this.pngDialogService, { invoiceId: invoice.invoiceId });
  }

  onRowSelected(event: RowSelectedEvent) {
    if (event.node.isSelected()) {
      this.selectedInvoice = event.data as TestableInvoice;
    } else {
      this.selectedInvoice = null;
    }
  }

  async loadInvoices() {
    this.isLoading = true;
    this.lastResult = null;
    try {
      const statusFilter = this.selectedStatusFilter.value;
      const url = `${environment.apiRoot}api/quickbooks/GetTestableInvoices?count=${this.invoiceCount}&statusFilter=${statusFilter}`;
      const response = await firstValueFrom(this.http.get<GetTestableInvoicesResponse>(url));
      this.invoices = response.invoices;
      this.selectedInvoice = null;
      this.toastr.success(`Loaded ${response.count} invoices`, 'QBO Test');
    } catch (err: any) {
      this.toastr.error(err.message || 'Failed to load invoices', 'QBO Test');
    } finally {
      this.isLoading = false;
    }
  }

  async postOrResyncInvoice() {
    if (!this.selectedInvoice) {
      this.toastr.warning('Please select an invoice first', 'QBO Test');
      return;
    }

    this.isLoading = true;
    this.lastResult = null;

    // Use different endpoint based on filter mode
    const isPosting = this.selectedStatusFilter.value === 'unposted';
    const endpoint = isPosting ? 'PostInvoiceTest' : 'ResyncInvoice';

    try {
      const url = `${environment.apiRoot}api/quickbooks/${endpoint}?invoiceHeaderId=${this.selectedInvoice.invoiceId}`;
      const response = await firstValueFrom(this.http.post<PostInvoiceResponse>(url, {}));
      this.lastResult = response;

      if (response.status === 'success') {
        this.toastr.success(response.message, 'QBO Test');
        // Reload the grid to show updated statuses for all posted invoices
        // (posting can affect multiple invoices on the same shipment)
        await this.loadInvoices();
      } else {
        this.toastr.error(response.message, 'QBO Test');
      }
    } catch (err: any) {
      this.lastResult = { status: 'error', message: err.message || 'Request failed', invoiceId: this.selectedInvoice.invoiceId };
      this.toastr.error(err.message || 'Failed to post invoice', 'QBO Test');
    } finally {
      this.isLoading = false;
    }
  }

  get canPostOrResync(): boolean {
    return this.selectedInvoice != null && !this.isLoading;
  }

  get actionButtonLabel(): string {
    return this.selectedStatusFilter.value === 'unposted' ? 'Post to QBO' : 'Resync to QBO';
  }

  get actionButtonIcon(): string {
    return this.selectedStatusFilter.value === 'unposted' ? 'pi pi-upload' : 'pi pi-sync';
  }
}
